import React from 'react';
import type { InsightAction } from '../../api/insights';

const css = `
  .insight-action-btn {
    background: linear-gradient(90deg, #4C64D9 0%, #7E47CC 100%);
    border: none;
    border-radius: 6px;
    height: 36px;
    padding: 0 14px;
    font-size: 14px;
    font-weight: 600;
    color: #fff;
    cursor: pointer;
    display: inline-flex;
    align-items: center;
    position: relative;
    overflow: hidden;
    transition: box-shadow 0.25s ease;
  }
  .insight-action-btn:hover:not(:disabled) {
    box-shadow: -1px 4px 8px rgba(76, 100, 217, 0.35), 1px 4px 8px rgba(126, 71, 204, 0.35);
  }
  .insight-action-btn:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
  .insight-action-btn-inner {
    position: relative;
    z-index: 1;
    display: inline-flex;
    align-items: center;
    gap: 6px;
  }
`;

export interface InsightActionButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  action?: InsightAction;
  onOpenChat?: (prompt: string) => void;
  onNavigate?: (url: string) => void;
  disabled?: boolean;
  icon?: React.ReactNode;
}

export const InsightActionButton = React.forwardRef<HTMLButtonElement, InsightActionButtonProps>(
  (
    {
      action,
      onOpenChat,
      onNavigate,
      disabled,
      icon,
      children,
      onClick,
      className,
      ...buttonProps
    },
    ref
  ) => {
    const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
      onClick?.(event);
      if (event.defaultPrevented || !action) return;
      if (action.intent === 'open_chat_with_prompt') {
        if (onOpenChat) onOpenChat(action.payload);
        else
          window.dispatchEvent(
            new CustomEvent('lognai:open-ai-chat', { detail: { prompt: action.payload } })
          );
      } else if (action.intent === 'navigate') {
        onNavigate?.(action.payload);
      }
    };
    return (
      <>
        <style>{css}</style>
        <button
          {...buttonProps}
          ref={ref}
          type="button"
          onClick={handleClick}
          disabled={disabled}
          className={`insight-action-btn ${className ?? ''}`}
        >
          <span className="insight-action-btn-inner">
            {icon && <span aria-hidden="true">{icon}</span>}
            {action?.label ?? children}
          </span>
        </button>
      </>
    );
  }
);
InsightActionButton.displayName = 'InsightActionButton';
